import { z } from 'zod';
/**
 * Guest play links (WS-23), part one: the vocabulary — id shape, name, colour, the socket
 * allowlist, and the one name resolver every games surface uses.
 *
 * A **leaf** on purpose: `index.ts` needs `guestNameSchema` for `GameMeta.guestNames`, and
 * the wire contracts in `guest.ts` need `gameMetaSchema` from `index.ts`. Splitting the
 * vocabulary out makes that a straight line (`guest` → `index` → `guestCore`) instead of a
 * cycle, which under ESM's hoisted imports would build one of the schemas against a
 * half-evaluated module and leave it `undefined`. Nothing here may import `./index.js`.
 *
 * ## What a guest play link is
 *
 * A family member mints one URL for a game and sends it to somebody outside the household,
 * who plays that match with no account at all. The link is **claim-on-first-open**: whoever
 * opens it first takes seat 2 and keeps it. That is deliberately also how "play with the
 * randos at game night" works — there is no invite/accept handshake and nothing to configure
 * per recipient.
 *
 * ## Why the guest id is allocated at *mint* time, not at claim time
 *
 * Seat 2 has to be a real id the moment the game exists, because every kind's `derive()`
 * reads `playerIds` positionally and a move by an id not in the tuple is an illegal move.
 * The claim, though, happens in the guest's browser — and a guest may not open the `games`
 * registry (see `parseGuestDocName`), so *nothing* on the guest's side can write seat 2 into
 * `GameMeta`. Minting therefore hands the creator's own client a fresh `guestId` along with
 * the token, and the creator — authenticated, and right there — writes
 * `playerIds: [me, guestId]` itself. Claiming then only *binds a session* to an id that was
 * already public. The id is not a secret; the token is.
 *
 * The guest's chosen **name** is the one thing still unknown at mint time, so it travels the
 * other way: the guest writes it into the match doc's seat map (`GAME_SEAT_KEY`), the one doc
 * they are allowed to write, and any member viewing the board mirrors it up into the
 * registry's `guestNames` for the surfaces that never open a match doc. Until that mirror
 * runs those surfaces say "Guest" — correct and readable, never a raw id.
 */
/**
 * Guest ids are namespaced so they can never collide with a member id (a `users.id` uuid).
 *
 * Underscore, not the colon a doc-name prefix would use, and that is load-bearing: a guest
 * id is substituted into `gameUserDocName()` for hidden-information kinds, and
 * `game:<id>:user:guest:<random>` would blow past `syncDocNameSchema`'s three-extra-segment
 * cap and fail `PER_MATCH_USER_DOC_PATTERN`'s capture. `guest_<random>` matches the segment
 * alphabet `[A-Za-z0-9_-]` those two already share, so a guest's per-user doc is enforced by
 * the *existing* rule — no new matcher, no widening of the doc grammar.
 */
export const GUEST_ID_PREFIX = 'guest_';
export const guestIdSchema = z
    .string()
    .regex(/^guest_[A-Za-z0-9_-]{8,48}$/, 'expected guest_<random>');
export function isGuestId(id) {
    return id.startsWith(GUEST_ID_PREFIX);
}
/** The name a guest gets when they skip the name field — naming yourself is optional. */
export const DEFAULT_GUEST_NAME = 'Guest';
export const guestNameSchema = z.string().trim().min(1).max(40);
/**
 * Guests render in one neutral slate rather than being handed a `MEMBER_COLORS` entry.
 * Member colours mean "this is a person in this household" everywhere else in the app;
 * lending one to a stranger would make the roster read wrong, and there is no free slot to
 * hand out anyway (the palette is per household, a guest exists for one match). Slate-500
 * clears 4.5:1 in every shipped theme — the same bar the member palette meets.
 */
export const GUEST_COLOR = '#64748b';
/** Public route the recipient opens; rendered outside the family chrome (`router.tsx`). */
export function guestPlayPath(token) {
    return `/play/${token}`;
}
/* -------------------------------------------------------------------------- */
/* The socket rule                                                             */
/* -------------------------------------------------------------------------- */
/**
 * The two — and only two — doc-name shapes a guest may ever open, parsed into the game they
 * concern: the shared match doc `game:<id>`, and their own hidden-state doc
 * `game:<id>:user:<guestId>`.
 *
 * Returns `null` for anything else, which the authorizer turns into a flat refusal. Written
 * as an allowlist rather than a denylist on purpose: a doc name added anywhere in the app
 * later is inaccessible to guests by default, and stays that way until somebody deliberately
 * teaches this function about it.
 *
 * `owner` is the `<guestId>` segment for the per-user shape and `null` for the shared doc.
 * The caller must still check it equals the connecting guest — this function only says "this
 * name is *about* a game", never "it belongs to you".
 */
export function parseGuestDocName(docName) {
    const match = /^game:([A-Za-z0-9_-]{1,64})(?::user:([A-Za-z0-9_-]{1,64}))?$/.exec(docName);
    if (!match)
        return null;
    const gameId = match[1];
    if (!gameId)
        return null;
    return { gameId, owner: match[2] ?? null };
}
/**
 * One resolver for "who is seat N", shared by every games surface (board headers, the games
 * index, the archive card, the replay scrubber, the timeline entry "Battleship: Dan sank
 * Sam"). The fallback chain is: household member → the guest's chosen name → "Guest".
 *
 * It is **total** — an id nobody recognises renders as a person, never as a raw id and never
 * as a throw. That was already true of `useHouseholdRoster().getMember` before WS-23 (it fell
 * back to "Member" plus a hashed colour); this adds the middle step and the neutral guest
 * colour, so the household palette keeps meaning "member of this household".
 */
export function resolveGamePlayer(playerId, roster, guestNames) {
    const member = roster.find((candidate) => candidate.id === playerId);
    if (member)
        return { ...member, isGuest: false };
    return {
        id: playerId,
        name: guestNames?.[playerId] ?? DEFAULT_GUEST_NAME,
        color: GUEST_COLOR,
        isGuest: true,
    };
}
//# sourceMappingURL=guestCore.js.map